/**
 * Page Performance → Page Detail (`?page=/route`): one page in the scope,
 * top to bottom — header, six load-time tiles with their meanings, "In
 * plain words" and why the page has its colour, the thin-data warning, six
 * browser tiles with a verdict each, the load-time chart, "Who and how",
 * the API calls the page makes, and the same page in other companies.
 */
import { useMemo } from 'react'
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Divide,
  FileSearch,
  Gauge,
  Info,
  MousePointerClick,
  Move,
  Paintbrush,
  Plug,
  Server,
  Timer,
} from 'lucide-react'
import { DataTable } from '../table/DataTable'
import { extractApiError } from '../../lib/api'
import { formatNumber } from '../../lib/format'
import { useDetail, viewerTimeZone } from '../../lib/page-perf/api-client'
import {
  STATUS_LABEL,
  browserVerdict,
  compareShort,
  countOf,
  deltaOf,
  formatBytes,
  formatCls,
  formatMs,
  formatPercent,
  levelFor,
  limitTriple,
  normaliseLimits,
  pageNameForRoute,
  plainWordsSegments,
  scopePhrase,
  statusReasonText,
  thinDataText,
  usersExactFrom,
} from '../../lib/page-perf/view-model'
import { LoadTimeChart } from './LoadTimeChart'
import {
  DeltaLine,
  FigureTile,
  FilterSpinner,
  InfoDot,
  PanelEmpty,
  PanelError,
  PanelLoading,
  PerfCard,
  PerfStatusBadge,
} from './PerfUi'
import { tintOf, toneOf } from './perf-tones'

const isNum = (v) => typeof v === 'number' && Number.isFinite(v)

function SplitRow({ label, sub, value, share }) {
  return (
    <div className="flex min-w-0 items-center gap-3 py-1.5">
      <div className="min-w-0 flex-1">
        <p className="m-0 truncate text-sm text-[color:var(--color-ink)]">{label}</p>
        {share != null ? (
          <span aria-hidden="true" className="mt-1 block h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--color-rule)]">
            <span className="block h-full rounded-full bg-[color:var(--color-accent)]" style={{ width: `${Math.max(2, share * 100)}%` }} />
          </span>
        ) : null}
      </div>
      <div className="shrink-0 text-right">
        <p className="m-0 text-sm tabular-nums text-[color:var(--color-ink-strong)]">{value}</p>
        {sub ? <p className="m-0 text-xs text-[color:var(--color-ink-muted)]">{sub}</p> : null}
      </div>
    </div>
  )
}

function Eyebrow({ children }) {
  return <p className="m-0 mb-1 mt-3 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--color-ink-subtle)] first:mt-0">{children}</p>
}

function WhoAndHow({ who }) {
  const total = (who?.byDevice ?? []).reduce((s, d) => s + (d.loads ?? 0), 0) || 1
  const browserTotal = (who?.byBrowser ?? []).reduce((s, d) => s + (d.loads ?? 0), 0) || 1
  const osTotal = (who?.byOs ?? []).reduce((s, d) => s + (d.loads ?? 0), 0) || 1
  const hasAny = (who?.byDevice ?? []).length > 0
  return (
    <PerfCard title="Who and how" note="Slow for everyone, or for one way of using it?">
      {!hasAny ? (
        <p className="m-0 text-sm text-[color:var(--color-ink-muted)]">No opens to split in this period.</p>
      ) : (
        <>
          <Eyebrow>By device</Eyebrow>
          {(who.byDevice ?? []).map((d) => (
            <SplitRow key={d.device} label={d.device || 'Unknown'} value={countOf(d.loads ?? 0, 'load')} sub={`P95 ${formatMs(d.p95Ms)}`} share={(d.loads ?? 0) / total} />
          ))}
          <Eyebrow>By browser</Eyebrow>
          {(who.byBrowser ?? []).map((d) => (
            <SplitRow key={d.browser} label={d.browser || 'Unknown'} value={countOf(d.loads ?? 0, 'load')} sub={`P95 ${formatMs(d.p95Ms)}`} share={(d.loads ?? 0) / browserTotal} />
          ))}
          {(who.byOs ?? []).length ? <Eyebrow>By operating system</Eyebrow> : null}
          {(who.byOs ?? []).map((d) => (
            <SplitRow key={d.os} label={d.os || 'Unknown'} value={countOf(d.loads ?? 0, 'load')} share={(d.loads ?? 0) / osTotal} />
          ))}
        </>
      )}
      <div className="mt-3 rounded-lg bg-[color:var(--color-canvas)] px-3 py-2 text-xs leading-relaxed text-[color:var(--color-ink-muted)]">
        <p className="m-0">
          Full loads: <b className="text-[color:var(--color-ink-strong)]">{formatNumber(who?.full?.loads ?? 0)}</b> (P95 {formatMs(who?.full?.p95Ms)}) · In-app
          navigations: <b className="text-[color:var(--color-ink-strong)]">{formatNumber(who?.spa?.loads ?? 0)}</b> (P95 {formatMs(who?.spa?.p95Ms)})
        </p>
        <p className="m-0 mt-1">
          {countOf(who?.abandoned?.loads ?? 0, 'open')} left before {(who?.abandoned?.loads ?? 0) === 1 ? 'it' : 'they'} finished (not in the figures)
          {isNum(who?.capped?.loads) ? ` · ${countOf(who.capped.loads, 'open')} capped at 20 s (included — real slow opens)` : ''}
        </p>
        <p className="m-0 mt-1">A slow full load with fast in-app navigations points at the server or the first paint, not the page&rsquo;s own work.</p>
      </div>
    </PerfCard>
  )
}

function apiColumns(limits) {
  return [
    {
      key: 'endpoint',
      header: 'Endpoint',
      minWidth: 260,
      pinnable: true,
      sortAccessor: (r) => r.path,
      searchAccessor: (r) => `${r.method ?? ''} ${r.path}`,
      exportValue: (r) => `${r.method ?? ''} ${r.path}`.trim(),
      render: (r) => (
        <div className="flex min-w-0 max-w-[340px] items-center gap-2">
          <span className="shrink-0 rounded border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-1.5 py-0.5 text-[11px] font-semibold text-[color:var(--color-ink-muted)]">
            {r.method ?? '—'}
          </span>
          <code className="min-w-0 truncate text-xs text-[color:var(--color-ink)]" title={r.path}>
            {r.path}
          </code>
        </div>
      ),
    },
    {
      key: 'perLoad',
      header: 'Per load',
      subheader: 'calls per open',
      align: 'right',
      minWidth: 100,
      sortAccessor: (r) => r.perLoad ?? 0,
      exportValue: (r) => r.perLoad ?? '',
      cellClassName: 'tabular-nums',
      render: (r) => (isNum(r.perLoad) ? formatNumber(r.perLoad, { maximumFractionDigits: 1 }) : '—'),
    },
    {
      key: 'calls',
      header: 'Calls',
      subheader: 'in the period',
      align: 'right',
      minWidth: 90,
      sortAccessor: (r) => r.calls ?? 0,
      exportValue: (r) => r.calls ?? '',
      cellClassName: 'tabular-nums',
      render: (r) => formatNumber(r.calls ?? 0),
    },
    {
      key: 'avgMs',
      header: 'Average',
      subheader: 'one call',
      align: 'right',
      minWidth: 100,
      sortAccessor: (r) => r.avgMs ?? -1,
      exportValue: (r) => (isNum(r.avgMs) ? Math.round(r.avgMs) : ''),
      cellClassName: 'tabular-nums',
      render: (r) => formatMs(r.avgMs),
    },
    {
      key: 'p95Ms',
      header: 'Slowest 5 of 100 (P95)',
      subheader: 'one call',
      align: 'right',
      minWidth: 150,
      sortAccessor: (r) => r.p95Ms ?? -1,
      exportValue: (r) => (isNum(r.p95Ms) ? Math.round(r.p95Ms) : ''),
      cellClassName: 'tabular-nums',
      render: (r) => <b className="text-[color:var(--color-ink-strong)]">{formatMs(r.p95Ms)}</b>,
    },
    {
      key: 'firstByteMs',
      header: 'First byte',
      subheader: 'wait until the reply started',
      align: 'right',
      minWidth: 130,
      sortAccessor: (r) => r.firstByteMs ?? -1,
      exportValue: (r) => (isNum(r.firstByteMs) ? Math.round(r.firstByteMs) : ''),
      cellClassName: 'tabular-nums text-[color:var(--color-ink-muted)]',
      render: (r) => (isNum(r.firstByteMs) ? formatMs(r.firstByteMs) : <span title="The browser did not report it">not reported</span>),
    },
    {
      key: 'sizeBytes',
      header: 'Size',
      subheader: 'reply, average',
      align: 'right',
      minWidth: 100,
      sortAccessor: (r) => r.sizeBytes ?? -1,
      exportValue: (r) => (isNum(r.sizeBytes) ? Math.round(r.sizeBytes) : ''),
      cellClassName: 'tabular-nums text-[color:var(--color-ink-muted)]',
      render: (r) => (isNum(r.sizeBytes) ? formatBytes(r.sizeBytes) : <span title="The browser did not report it">not reported</span>),
    },
    {
      key: 'failedRate',
      header: 'Errors',
      subheader: 'calls that failed',
      headerTitle: 'The share of these calls that failed: a server error (5xx), a timeout (408), too many requests (429) or no reply — a permission or not-found reply (403, 404 …) does not count.',
      align: 'right',
      minWidth: 110,
      sortAccessor: (r) => r.failedRate ?? -1,
      exportValue: (r) => (isNum(r.failedRate) ? formatPercent(r.failedRate) : ''),
      render: (r) => {
        const level = levelFor(r.failedRate, limits.errors)
        const bad = level && level !== 'healthy'
        return (
          <span className={'tabular-nums ' + (bad ? 'font-semibold text-[color:var(--color-danger-text)]' : 'text-[color:var(--color-ink-muted)]')}>
            {formatPercent(r.failedRate)}
            {isNum(r.failed) && r.failed > 0 ? <span className="block text-xs font-normal">{countOf(r.failed, 'call')}</span> : null}
          </span>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      pinnedRight: true,
      minWidth: 120,
      sortable: false,
      exportValue: (r) => STATUS_LABEL[r.status] ?? 'Not judged',
      render: (r) => <PerfStatusBadge status={r.status} />,
    },
  ]
}

function ApiCalls({ detail, limits, period, where }) {
  const columns = useMemo(() => apiColumns(limits), [limits])
  const rows = detail?.apiCalls?.rows ?? null
  const summary = detail?.apiCallsSummary
  const slowest = summary?.slowest
  return (
    <PerfCard
      id="page-perf-api-calls"
      title={
        <span className="inline-flex items-center gap-2">
          <Plug size={16} className="text-[color:var(--color-accent-strong)]" /> API calls on this page
        </span>
      }
      note="Every API request the page makes while it opens · averages over the period · judged by the page’s own limits"
    >
      {rows == null ? (
        <PanelEmpty
          icon={Info}
          title={period === '90d' ? 'API calls are available for the last 30 days' : 'API calls were not sent for this period'}
          body="The calls come from the raw opens, which are kept for 30 days. Pick Last 30 days or shorter to see them."
        />
      ) : (
        <>
          {summary ? (
            <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                ['Endpoints', formatNumber(summary.endpoints ?? rows.length)],
                ['Calls per open', isNum(summary.callsPerLoad) ? formatNumber(summary.callsPerLoad, { maximumFractionDigits: 1 }) : '—'],
                ['Calls in the period', formatNumber(summary.calls ?? 0)],
                ['Slowest call (P95)', slowest?.path ? `${slowest.method ?? ''} ${slowest.path} · ${formatMs(slowest.p95Ms)}` : '—'],
              ].map(([label, value]) => (
                <div key={label} className="min-w-0 rounded-lg border border-[color:var(--color-rule)] px-3 py-2">
                  <p className="m-0 truncate text-sm font-semibold tabular-nums text-[color:var(--color-ink-strong)]" title={value}>
                    {value}
                  </p>
                  <p className="m-0 text-xs text-[color:var(--color-ink-muted)]">{label}</p>
                </div>
              ))}
            </div>
          ) : null}
          <DataTable
            tableId="page-perf-api-calls"
            columns={columns}
            rows={rows}
            getRowId={(r) => `${r.method ?? ''} ${r.path}`}
            searchPlaceholder="Search endpoint"
            defaultSort={[{ key: 'p95Ms', dir: 'desc' }]}
            exportBaseName="page-performance-api-calls"
            exportTitle={`Page Performance — API calls on ${detail?.route ?? 'this page'}`}
            emptyState={{ icon: Plug, title: `This page made no API calls ${where}` }}
          />
          <p className="m-0 mt-2 text-xs text-[color:var(--color-ink-subtle)]">
            Timed in the browser, so each figure includes the network both ways. The split into server time and database time
            arrives when the server records it.
          </p>
        </>
      )}
    </PerfCard>
  )
}

function OtherCompanies({ rows, go }) {
  return (
    <PerfCard title="Same page, other companies" note="Is it the page or the data? This list ignores the company filter above — it is every company that opened this page in the period.">
      {rows.length === 0 ? (
        <p className="m-0 text-sm text-[color:var(--color-ink-muted)]">No company opened this page in the period.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] border-separate border-spacing-0 text-sm">
            <thead>
              <tr className="text-left text-xs text-[color:var(--color-ink-muted)]">
                <th className="border-b border-[color:var(--color-rule)] px-2 py-2 font-medium" style={{ minWidth: 180 }}>Company</th>
                <th className="border-b border-[color:var(--color-rule)] px-2 py-2 text-right font-medium">Page loads</th>
                <th className="border-b border-[color:var(--color-rule)] px-2 py-2 text-right font-medium">People</th>
                <th className="border-b border-[color:var(--color-rule)] px-2 py-2 text-right font-medium">Average open</th>
                <th className="border-b border-[color:var(--color-rule)] px-2 py-2 text-right font-medium">P95</th>
                <th className="border-b border-[color:var(--color-rule)] px-2 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.tenantId ?? o.name} style={o.selected ? { backgroundColor: 'var(--color-accent-bg)' } : undefined}>
                  <td className="border-b border-[color:var(--color-rule)] px-2 py-2">
                    {o.selected || !o.tenantId ? (
                      <span className="font-semibold text-[color:var(--color-ink-strong)]">
                        {o.name ?? 'Unnamed company'}
                        {o.selected ? <span className="ml-1 text-xs font-normal text-[color:var(--color-ink-muted)]">(selected)</span> : null}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => go({ organization: o.tenantId })}
                        title="Narrow every tab to this company, staying on this page"
                        className="cursor-pointer text-left font-semibold text-[color:var(--color-accent-strong)] hover:underline"
                      >
                        {o.name ?? 'Unnamed company'}
                      </button>
                    )}
                  </td>
                  <td className="border-b border-[color:var(--color-rule)] px-2 py-2 text-right tabular-nums">{formatNumber(o.loads ?? 0)}</td>
                  <td className="border-b border-[color:var(--color-rule)] px-2 py-2 text-right tabular-nums">{formatNumber(o.users ?? 0)}</td>
                  <td className="border-b border-[color:var(--color-rule)] px-2 py-2 text-right tabular-nums">{formatMs(o.avgMs)}</td>
                  <td className="border-b border-[color:var(--color-rule)] px-2 py-2 text-right font-semibold tabular-nums">{formatMs(o.p95Ms)}</td>
                  <td className="border-b border-[color:var(--color-rule)] px-2 py-2">{o.status ? <PerfStatusBadge status={o.status} /> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="m-0 mt-2 text-xs text-[color:var(--color-ink-subtle)]">
        The same code fast for one company and slow for another is usually a difference in how much data each has, not a problem in the page.
      </p>
    </PerfCard>
  )
}

export function PerfDetailTab({ ctx }) {
  const { scope, state, go, slot: fallbackSlot, companyName, openGuide, openLoadHelp } = ctx
  const route = state.page
  const query = useDetail(scope, route)
  const d = query.data
  const limits = useMemo(() => normaliseLimits(d?.limits), [d?.limits])
  const slot = d?.scope?.slot === 'day' || d?.scope?.slot === 'hour' ? d.scope.slot : fallbackSlot
  const where = scopePhrase(d?.scope?.organizationName ?? companyName, scope.period)
  const usersExact = usersExactFrom(d?.scope)
  const vsWhat = compareShort(scope.compare)
  const name = pageNameForRoute(route)

  const t = d?.tiles ?? {}
  const v = (k) => t[k]?.v
  const prevText = (k, fmt) => (isNum(t[k]?.prev) ? fmt(t[k].prev) : vsWhat)
  const measured = v('measuredLoads') ?? v('loads')
  const users = v('users') ?? d?.users
  const helpFigures = {
    route,
    loads: measured,
    users,
    avgMs: v('avgMs'),
    p50Ms: v('p50Ms'),
    p95Ms: v('p95Ms'),
    p99Ms: v('p99Ms'),
    waitedMs: isNum(v('avgMs')) && isNum(measured) ? v('avgMs') * measured : null,
    opens: d?.opensForHelp ?? null,
    opensExact: d?.opensForHelpExact !== false,
    usersExact,
  }

  const header = (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="m-0 flex flex-wrap items-center gap-2 font-display text-xl font-semibold text-[color:var(--color-ink-strong)]">
          <span className="min-w-0 break-words">{name}</span>
          {d ? <PerfStatusBadge status={d.status} /> : null}
        </h2>
        <p className="m-0 mt-0.5 break-all font-mono text-xs text-[color:var(--color-ink-muted)]">{route}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <FilterSpinner show={query.isFetching && query.isPlaceholderData} />
        <button
          type="button"
          onClick={() => openGuide('load')}
          className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 text-sm font-medium text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]"
        >
          <Info size={14} className="text-[color:var(--color-accent-strong)]" /> How these are measured
        </button>
        <button
          type="button"
          onClick={() => go({ tab: 'pages' })}
          className="inline-flex h-9 cursor-pointer items-center rounded-lg px-3 text-sm font-medium text-[color:var(--color-accent-strong)] hover:bg-[color:var(--color-accent-bg)]"
        >
          ← All pages
        </button>
      </div>
    </div>
  )

  if (query.isLoading) {
    return (
      <div className="min-w-0 space-y-4">
        {header}
        <PerfCard>
          <PanelLoading lines={5} label={`Loading ${name}`} />
        </PerfCard>
      </div>
    )
  }
  if (query.isError && !d) {
    return (
      <div className="min-w-0 space-y-4">
        {header}
        <PanelError message={`${name} (${route}) ${where} could not be loaded — ${extractApiError(query.error).message}`} onRetry={() => query.refetch()} />
      </div>
    )
  }
  if (!d || ((v('loads') ?? 0) === 0 && !d.status)) {
    return (
      <div className="min-w-0 space-y-4">
        {header}
        <PanelEmpty
          icon={FileSearch}
          title={`No opens of ${name} ${where}`}
          body={
            (d?.otherOrganizations ?? []).length
              ? 'Other companies did open it — see below, or pick one of them in the company filter.'
              : 'Try a longer period, or another company.'
          }
        />
        {/* The cross-company list ignores the company filter, so it can have
         *  rows even when the selected company has none — "is it the page or
         *  the data?" is exactly the question an empty company raises. */}
        {(d?.otherOrganizations ?? []).length ? <OtherCompanies rows={d.otherOrganizations} go={go} /> : null}
      </div>
    )
  }

  const b = d.browser ?? {}
  const p95Level = levelFor(v('p95Ms'), limits.load)
  const thin = d.thin ?? (isNum(measured) && measured < 20)
  const thinText = thin ? thinDataText(measured) : null
  const abandoned = v('abandoned') ?? d.whoAndHow?.abandoned?.loads
  const segments = plainWordsSegments({
    loads: measured,
    users,
    p50Ms: v('p50Ms'),
    p75Ms: v('p75Ms'),
    p95Ms: v('p95Ms'),
    p99Ms: v('p99Ms'),
    avgMs: v('avgMs'),
    usersExact,
    abandoned,
  })
  const tz = d.scope?.tz || viewerTimeZone()
  const tiles = [
    { kind: 'ttfb', label: 'TTFB (full loads)', icon: Server, value: formatMs(b.ttfbP95Ms), level: b.levels?.ttfbP95, figure: b.ttfbP95Ms, title: 'Time To First Byte, slowest 5 of 100 full loads — the wait before the server starts answering.', guide: 'ttfb' },
    { kind: 'fcp', label: 'FCP (full loads)', icon: Paintbrush, value: formatMs(b.fcpP75Ms), figure: b.fcpP75Ms, title: 'First Contentful Paint, 3 of 4 full loads within this — the first sign of life on screen.', guide: 'fcp' },
    { kind: 'lcp', label: 'LCP P75 (full loads)', icon: BarChart3, value: formatMs(b.lcpP75Ms), level: b.levels?.lcpP75, figure: b.lcpP75Ms, title: 'Largest Contentful Paint — when the page looks loaded.', guide: 'lcp' },
    { kind: 'cls', label: 'CLS P75', icon: Move, value: formatCls(b.clsP75), figure: b.clsP75, title: 'Cumulative Layout Shift — how much the content jumped while loading. A score, not a time.', guide: 'cls' },
    { kind: 'inp', label: 'INP P75', icon: MousePointerClick, value: formatMs(b.inpP75Ms), figure: b.inpP75Ms, title: 'Interaction to Next Paint — how quickly the page reacts to a click.', guide: 'inp' },
    { kind: 'errors', label: 'Loads with a failed request', icon: AlertTriangle, value: formatPercent(b.failedRate ?? v('failedRate')), level: b.levels?.errorRate, figure: b.failedRate ?? v('failedRate'), title: 'The share of opens in which at least one API call failed: a server error (5xx), a timeout (408), too many requests (429) or no reply — a permission or not-found reply (403, 404 …) does not count.', guide: 'err' },
  ]

  return (
    <div className="min-w-0 space-y-4">
      {header}

      <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
        <FigureTile
          label="Page loads"
          icon={Activity}
          value={formatNumber(v('loads') ?? 0)}
          meaning={`${isNum(users) ? `${countOf(users, 'person', 'people')}${usersExact ? '' : ' (per day, added up)'} · ` : ''}each open has its own stopwatch time${isNum(abandoned) && abandoned > 0 ? ` · ${countOf(abandoned, 'open')} left before ${abandoned === 1 ? 'it' : 'they'} finished` : ''}`}
          compare={<DeltaLine delta={deltaOf(v('loads'), t.loads?.prev, { neutral: true })} vs={prevText('loads', (n) => countOf(n, 'open'))} />}
        />
        <FigureTile
          label="Average open"
          icon={Divide}
          value={formatMs(v('avgMs'))}
          meaning={isNum(measured) && measured > 0 ? `the ${formatNumber(measured)} times added up ÷ ${formatNumber(measured)}` : 'every finished open added up ÷ the opens'}
          compare={<DeltaLine delta={deltaOf(v('avgMs'), t.avgMs?.prev)} vs={prevText('avgMs', formatMs)} />}
        />
        <FigureTile
          label="Typical open (P50)"
          icon={Timer}
          value={formatMs(v('p50Ms'))}
          meaning="half of the opens were faster than this — what most people feel"
          compare={<DeltaLine delta={deltaOf(v('p50Ms'), t.p50Ms?.prev)} vs={prevText('p50Ms', formatMs)} />}
        />
        <FigureTile
          label="3 of 4 faster (P75)"
          icon={Timer}
          value={formatMs(v('p75Ms'))}
          meaning="3 of every 4 opens finished within this"
          compare={<DeltaLine delta={deltaOf(v('p75Ms'), t.p75Ms?.prev)} vs={prevText('p75Ms', formatMs)} />}
        />
        <FigureTile
          label="Slowest 5 of 100 (P95)"
          icon={BarChart3}
          tone={p95Level ? toneOf(p95Level) : undefined}
          value={formatMs(v('p95Ms'))}
          info={<InfoDot onClick={() => openLoadHelp(helpFigures)} label="How the load times are worked out" />}
          meaning={`95 of 100 opens within this — the colour uses it · limits ${limitTriple(limits.load)}`}
          compare={<DeltaLine delta={deltaOf(v('p95Ms'), t.p95Ms?.prev)} vs={prevText('p95Ms', formatMs)} />}
        />
        <FigureTile
          label="Slowest 1 of 100 (P99)"
          icon={Gauge}
          value={formatMs(v('p99Ms'))}
          meaning="the worst open anyone had — 99 of 100 were within this"
          compare={<DeltaLine delta={deltaOf(v('p99Ms'), t.p99Ms?.prev)} vs={prevText('p99Ms', formatMs)} />}
        />
      </div>

      <div className={'grid min-w-0 grid-cols-1 gap-4 ' + (thinText ? 'lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : '')}>
        <PerfCard>
          <p className="m-0 text-sm leading-relaxed text-[color:var(--color-ink)]">
            <b>In plain words:</b>{' '}
            {segments.map((s, i) => (typeof s === 'string' ? <span key={i}>{s}</span> : <b key={i}>{s.b}</b>))}{' '}
            {statusReasonText({ status: d.status, statusReason: d.statusReason, limits, p95Ms: v('p95Ms') })}{' '}
            <button type="button" onClick={() => openLoadHelp(helpFigures)} className="cursor-pointer font-medium text-[color:var(--color-accent-strong)] hover:underline">
              How this is worked out →
            </button>
          </p>
        </PerfCard>
        {thinText ? (
          <div role="note" className="min-w-0 rounded-xl border px-4 py-3 text-sm" style={{ backgroundColor: tintOf(toneOf('warning'), 12), borderColor: tintOf(toneOf('warning'), 40) }}>
            <p className="m-0">
              <b>⚠ </b>
              {thinText}
            </p>
          </div>
        ) : null}
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
        {tiles.map((tile) => {
          const verdict = browserVerdict(tile.kind, tile.figure, limits, tile.level)
          return (
            <FigureTile
              key={tile.kind}
              label={tile.label}
              icon={tile.icon}
              value={tile.value}
              tone={verdict.level && verdict.level !== 'healthy' ? toneOf(verdict.level) : undefined}
              title={tile.title}
              info={<InfoDot onClick={() => openGuide(tile.guide)} label={`What ${tile.label} means`} />}
              meaning={verdict.text}
            />
          )
        })}
      </div>
      <p className="m-0 -mt-2 text-xs text-[color:var(--color-ink-subtle)]">
        TTFB, FCP and LCP come from full loads only{isNum(b.fullLoads) ? ` (${countOf(b.fullLoads, 'full load')} in this period)` : ''} — an in-app navigation makes no document request.
      </p>

      <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-3">
        <PerfCard
          className="lg:col-span-2"
          title="Load time over the period"
          note={
            slot === 'day'
              ? 'P95 and average per day for this page · page loads as bars · days are UTC (today so far included)'
              : `P95 and average per hour for this page · page loads as bars · times in ${tz}`
          }
        >
          <LoadTimeChart slots={d.chart?.slots ?? []} restarts={d.chart?.restarts ?? []} slot={slot} limits={limits} emptyText={`No opens of ${name} ${where}`} />
        </PerfCard>
        <WhoAndHow who={d.whoAndHow} />
      </div>

      <ApiCalls detail={d} limits={limits} period={scope.period} where={where} />

      <OtherCompanies rows={d.otherOrganizations ?? []} go={go} />
    </div>
  )
}
